import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { AuthContext } from "../authcontext";
import { loginRequest } from "./authconfig";
import UnauthorizedLayout from "../../components/layouts/unauthorizedlayout";
import { Result } from "antd";
import { createUserIfNotExists } from "../../services/userservice";

interface AzureAuthProviderProps {
    children: React.ReactNode;
}

export const AzureAuthProvider: React.FC<AzureAuthProviderProps> = ({ children }) => {
    const { instance } = useMsal();

    const [userId, setUserId] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [displayName, setDisplayName] = useState<string | null>(null);
    const [userSyncFailed, setUserSyncFailed] = useState(false);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [roles, setRoles] = useState<string[]>([]);
    const [isReadOnly, setIsReadOnly] = useState<boolean>(true);

    const isAuthenticated = !!userId;

    /**
     * Trigger Azure AD login
     */
    const login = async () => {
        await instance.loginRedirect(loginRequest);
    };

    /**
     * Logout from Azure AD
     */
    const logout = async () => {
        localStorage.removeItem('BusinessUnitGuid');
        localStorage.removeItem('BusinessUnitName');
        localStorage.removeItem('BusinessUnitCode');
        await instance.logoutRedirect();
    };

    /**
     * Normalize Azure account into our generic auth model.
     * IMPORTANT:
     * - MSAL bootstrap (initialize + handleRedirectPromise) is already completed
     *   in AzureBootstrap.
     * - This effect MUST mark auth as ready regardless of login state.
     */
    useEffect(() => {
        const account = instance.getActiveAccount();

        if (account?.idTokenClaims) {
            const oid = account.idTokenClaims.oid as string | undefined;

            setUserId(oid ?? null);
            setEmail(account.username ?? null);
            setDisplayName(account.name ?? null);

            // Optional: backend user auto‑provisioning
            if (oid && account.name && account.username) {
                const cleanedName = account.name.replace(",", "").trim();
                const [lastName, ...firstParts] = cleanedName.split(" ");
                const firstName = firstParts.join(" ");

                createUserIfNotExists({
                    guid: oid,
                    firstName: firstName || cleanedName,
                    lastName: lastName || "",
                    email: account.username,
                  }).catch(() => {
                    setUserSyncFailed(true);
                  });
            }
        }
       
        setIsAuthReady(true);
    }, [instance]);

    /**
     * GLOBAL AUTH ENFORCEMENT
     * Since your app has NO public routes:
     * - If auth is ready AND no user exists → redirect to Azure login
     * - This replaces AuthEnforcer entirely
     */
    useEffect(() => {
        if (isAuthReady && !isAuthenticated) {
            login();
        }
    }, [isAuthReady, isAuthenticated]);

    if (userSyncFailed) {
        return (
            <UnauthorizedLayout>
                <Result
                    status="500"
                    title="Something Went Wrong"
                    subTitle="We couldn't verify your account. Please try again later or contact support."
                />
            </UnauthorizedLayout>
        );
    }

    /**
     * Block rendering until authentication is complete
     * (prevents partial UI or race conditions)
     */
    if (!isAuthenticated) {
        return null; // or a branded loading / splash screen
    }

    return (
        <AuthContext.Provider
            value={{
                userId,
                email,
                displayName,
                accessToken: null, //Azure AD does not provide long lived access token
                isAuthenticated,
                isAuthReady,
                userSyncFailed,
                sessionTimeRemaining: null,
                roles,
                isReadOnly,
                login,
                logout,
                maintainSession: async () => false,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};